/**
 * Thesis-related type definitions for structured investment theses.
 *
 * A structured thesis consists of:
 * - statement: The core investment thesis (required)
 * - pillars: Supporting arguments/evidence (optional, max 10 items)
 * - risks: Key investment risks (optional, max 10 items)
 */

/**
 * Core structured thesis data structure.
 * Used for both form data and parsed thesis content.
 */
export interface StructuredThesis {
  /** Core investment thesis statement (max 1000 characters) */
  statement: string;
  /** Supporting pillars for the thesis (max 10 items) */
  pillars: string[];
  /** Key risks for the thesis (max 10 items) */
  risks: string[];
}

/**
 * Form data structure for structured thesis input.
 * Has the same shape as StructuredThesis for use in forms.
 */
export interface StructuredThesisFormData extends StructuredThesis {}

/**
 * Request payload for creating a new position thesis.
 * At least one of thesis_text or structured_thesis is required.
 */
export interface ThesisCreateRequest {
  /** ID of the position snapshot */
  position_id: number;
  /** Structured thesis data (statement, pillars, risks) */
  structured_thesis?: StructuredThesis | null;
  /** Legacy plain text thesis (converted to JSON format with statement only) */
  thesis_text?: string | null;
  /** Stop-loss price target */
  stop_loss_price?: number | null;
  /** Take-profit price target */
  take_profit_price?: number | null;
  /** Conviction level 1-5 (1=Speculative, 5=High Conviction) */
  conviction_level?: number | null;
  /** Investment strategy duration */
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
  /** Entry price for watchlist thesis (optional) */
  entry_price?: number | null;
  /** Reminder date for watchlist thesis (optional) */
  remind_at?: string | null;
}

/**
 * Request payload for updating an existing position thesis.
 * All fields are optional. Creates a new version with changes applied.
 */
export interface ThesisUpdateRequest {
  /** Updated structured thesis data (statement, pillars, risks) */
  structured_thesis?: StructuredThesis | null;
  /** Updated thesis text */
  thesis_text?: string | null;
  /** Updated stop-loss price */
  stop_loss_price?: number | null;
  /** Updated take-profit price */
  take_profit_price?: number | null;
  /** Updated conviction level */
  conviction_level?: number | null;
  /** Updated investment duration */
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
  /** Entry price for watchlist thesis (optional) */
  entry_price?: number | null;
  /** Reminder date for watchlist thesis (optional) */
  remind_at?: string | null;
}

/**
 * Response from the position thesis API.
 * The thesis_text field contains JSON string with { statement, pillars?, risks? }
 * or { statement } for legacy theses.
 */
export interface PositionThesisResponse {
  /** Thesis ID */
  id: number;
  /** Position snapshot ID */
  position_id: number;
  /** User ID who owns this thesis */
  user_id: number;
  /**
   * JSON string containing structured thesis data.
   * Format: { "statement": "...", "pillars": [...], "risks": [...] }
   * Legacy format: { "statement": "..." }
   */
  thesis_text: string | null;
  /** Stop-loss price target */
  stop_loss_price: number | null;
  /** Take-profit price target */
  take_profit_price: number | null;
  /** Conviction level 1-5 */
  conviction_level: number | null;
  /** Investment strategy duration */
  duration: 'Core' | 'Growth' | 'Speculative' | null;
  /** Single primary theme (Q5 §6.2) */
  theme?: string | null;
  /** Last update timestamp */
  updated_at: string;
}

/**
 * Parsed structured thesis with optional fields.
 * Used when parsing thesis_text from the API.
 */
export interface ParsedStructuredThesis {
  statement?: string;
  pillars?: string[];
  risks?: string[];
}

/**
 * Empty structured thesis constant for initialization.
 */
export const EMPTY_STRUCTURED_THESIS: StructuredThesis = {
  statement: '',
  pillars: [],
  risks: [],
};

/**
 * Maximum length of a single pillar (catalyst) or risk entry.
 *
 * These are edited in a modal text area (see LongTextField) instead of a
 * one-line input, so the counter needs a bound. The backend caps the whole
 * serialized thesis at 5000 characters, so 500 per entry leaves generous room.
 */
export const THESIS_LIST_ITEM_MAX_LENGTH = 500;

/**
 * Parse thesis text from API response into a StructuredThesis.
 *
 * Handles:
 * - null/undefined input → returns empty structure
 * - Valid JSON → parses and returns structured data
 * - Invalid JSON → treats as plain text statement
 * - Legacy format (statement only) → fills in empty pillars/risks
 *
 * @param thesisText - The JSON string from thesis_text field
 * @returns Parsed StructuredThesis, or empty structure if parsing fails
 *
 * @example
 * ```ts
 * const parsed = parseThesisText(response.thesis_text);
 * console.log(parsed.statement);  // "Strong buy on AI momentum"
 * console.log(parsed.pillars);    // ["AI leadership", "Growing services"]
 * ```
 */
export function parseThesisText(thesisText: string | null | undefined): StructuredThesis {
  // Handle null/empty/undefined
  if (!thesisText || !thesisText.trim()) {
    return { ...EMPTY_STRUCTURED_THESIS };
  }

  try {
    // Try to parse as JSON
    const parsed = JSON.parse(thesisText) as ParsedStructuredThesis;

    // Return structured data with defaults for missing fields
    return {
      statement: parsed.statement || '',
      pillars: parsed.pillars || [],
      risks: parsed.risks || [],
    };
  } catch {
    // JSON parse failed - treat as plain text statement
    return {
      statement: thesisText,
      pillars: [],
      risks: [],
    };
  }
}

/**
 * Convert a StructuredThesis to a JSON string for API requests.
 *
 * @param thesis - The structured thesis to stringify
 * @returns JSON string representation
 *
 * @example
 * ```ts
 * const thesis = {
 *   statement: "Strong buy",
 *   pillars: ["AI leadership"],
 *   risks: ["China exposure"]
 * };
 * const json = stringifyThesis(thesis);
 * // '{"statement":"Strong buy","pillars":["AI leadership"],"risks":["China exposure"]}'
 * ```
 */
export function stringifyThesis(thesis: StructuredThesis): string {
  return JSON.stringify({
    statement: thesis.statement,
    pillars: thesis.pillars,
    risks: thesis.risks,
  });
}

/**
 * Type guard to check if a value is a valid StructuredThesis.
 *
 * @param value - Value to check
 * @returns True if value matches StructuredThesis structure
 */
export function isStructuredThesis(value: unknown): value is StructuredThesis {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const thesis = value as Record<string, unknown>;

  return (
    typeof thesis.statement === 'string' &&
    Array.isArray(thesis.pillars) &&
    thesis.pillars.every((item) => typeof item === 'string') &&
    Array.isArray(thesis.risks) &&
    thesis.risks.every((item) => typeof item === 'string')
  );
}
